/** ⌘K command palette.
 *
 * Jump targets, in result order:
 *  - Recent records (localStorage — written by PageHeader, see recents.ts)
 *  - Pages (the sidebar's SECTIONS, permission-filtered)
 *  - Open by ID (numeric query → direct record/booking/task links)
 *  - Live entity search: users (all three kinds), tour operators, tours —
 *    each behind its view permission, fanned out on a 300 ms debounce.
 *
 * No new dependencies: MUI Dialog + List, TanStack Query for the fan-out.
 * Mounted once in AppShell (needs the router + auth contexts).
 */
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as topsApi from '@/api/tourOperators';
import * as toursApi from '@/api/tours';
import * as usersApi from '@/api/users';
import type { UserKind } from '@/api/users';
import { usePermissions } from '@/hooks/usePermissions';
import { SECTIONS, isGroup } from '@/layout/SidebarMenu';

import { CREATE_TARGETS, ID_SHORTCUTS, matchAll, parseQuery, type IdKind } from './matching';
import { readRecents } from './recents';

interface PaletteItem {
  key: string;
  label: string;
  sub?: string;
  group: string;
  to: string;
}

interface NavTarget {
  label: string;
  area: string;
  to: string;
  perm?: string;
}

/** The sidebar flattened into jump targets ("Catalog · Fleet · Bus stops"). */
const NAV_TARGETS: NavTarget[] = SECTIONS.flatMap((section) =>
  section.entries.flatMap((e) =>
    isGroup(e)
      ? e.children.map((c) => ({
          label: c.label,
          area: `${section.label} · ${e.label}`,
          to: c.to,
          ...(c.perm ? { perm: c.perm } : {}),
        }))
      : [{ label: e.label, area: section.label, to: e.to, ...(e.perm ? { perm: e.perm } : {}) }],
  ),
);

const USER_KINDS: UserKind[] = ['system', 'staff', 'guest'];

export function CommandPalette({
  open,
  onOpen,
  onClose,
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K toggles, everywhere (including inside inputs — standard).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) onClose();
        else onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpen, onClose]);

  // Fresh state per open.
  useEffect(() => {
    if (open) {
      setInput('');
      setDebounced('');
      setSel(0);
    }
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(input.trim()), 300);
    return () => window.clearTimeout(t);
  }, [input]);

  // Entity fan-out gates; the ID and Create groups gate per-target via hasPerm.
  const canUsers = hasPerm('console_user_view');
  const canTops = hasPerm('console_top_view');
  const canTours = hasPerm('console_tours_view');

  const parsed = useMemo(() => parseQuery(debounced), [debounced]);

  const entityQuery = useQuery({
    queryKey: ['commandPalette', debounced],
    enabled: open && debounced.length >= 2 && !parsed.wantsCreate,
    staleTime: 30_000,
    queryFn: async () => {
      const [users, tops, tours] = await Promise.all([
        canUsers
          ? Promise.allSettled(
              USER_KINDS.map((kind) => usersApi.list({ kind, search: debounced, pageSize: 4 })),
            ).then((rs) => rs.flatMap((r) => (r.status === 'fulfilled' ? r.value.results : [])))
          : Promise.resolve([]),
        canTops
          ? topsApi.list({ search: debounced, pageSize: 5 }).then((r) => r.results).catch(() => [])
          : Promise.resolve([]),
        canTours
          ? toursApi.list({ search: debounced, pageSize: 5 }).then((r) => r.results).catch(() => [])
          : Promise.resolve([]),
      ]);
      return { users, tops, tours };
    },
  });

  const items = useMemo<PaletteItem[]>(() => {
    const { tokens, filterTokens } = parsed;
    const out: PaletteItem[] = [];

    const recents = readRecents()
      .filter((r) => matchAll(tokens, `${r.label} ${r.area}`) > 0)
      .slice(0, 8);
    out.push(
      ...recents.map((r) => ({
        key: `recent:${r.to}`,
        label: r.label,
        sub: r.area,
        group: 'Recent',
        to: r.to,
      })),
    );

    // Create quick-links — only on explicit create intent, filtered by the
    // remaining keywords (a bare "create"/"new" lists everything creatable).
    if (parsed.wantsCreate) {
      const creates = CREATE_TARGETS.filter((t) => hasPerm(t.perm))
        .map((t) => ({ t, s: matchAll(filterTokens, `${t.label} ${(t.keywords ?? []).join(' ')}`) }))
        .filter(({ s }) => s > 0)
        .sort((a, b) => b.s - a.s);
      out.push(
        ...creates.map(({ t }) => ({
          key: `create:${t.to}`,
          label: t.label,
          group: 'Create',
          to: t.to,
        })),
      );
    }

    const pages = NAV_TARGETS.filter((t) => !t.perm || hasPerm(t.perm))
      .map((t) => ({ t, s: matchAll(tokens, `${t.label} ${t.area}`) }))
      .filter(({ s }) => s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, tokens.length ? 6 : NAV_TARGETS.length);
    out.push(
      ...pages.map(({ t }) => ({
        key: `page:${t.to}`,
        label: t.label,
        sub: t.area,
        group: 'Pages',
        to: t.to,
      })),
    );

    // Open by ID — an integer, a UUID or a ticket id anywhere in the query, the
    // rest of the keywords narrowing which record types to offer.
    const pushIds = (kind: IdKind, id: string) => {
      ID_SHORTCUTS.filter((s) => s.kind === kind)
        .filter((s) => !s.perm || hasPerm(s.perm))
        .filter((s) => matchAll(filterTokens, `${s.label} ${(s.keywords ?? []).join(' ')}`) > 0)
        .forEach((s) => {
          const to = s.to(id);
          out.push({ key: `id:${to}`, label: `${s.label} #${id}`, group: 'Open by ID', to });
        });
    };
    if (parsed.intId) pushIds('int', parsed.intId);
    if (parsed.uuidId) pushIds('uuid', parsed.uuidId);
    if (parsed.ticketId) pushIds('ticket', parsed.ticketId);

    const data = debounced.length >= 2 ? entityQuery.data : undefined;
    if (data) {
      out.push(
        ...data.users.map((u) => ({
          key: `user:${u.id}`,
          label: `${u.first_name} ${u.last_name}`.trim() || u.email,
          sub: [u.email, u.tour_operator?.name ?? null, u.kind].filter(Boolean).join(' · '),
          group: 'Users',
          to: `/users/${u.id}`,
        })),
        ...data.tops.map((t) => ({
          key: `top:${t.id}`,
          label: t.name,
          sub: t.code ?? undefined,
          group: 'Tour operators',
          to: `/tour-operators/${t.id}`,
        })),
        ...data.tours.map((t) => ({
          key: `tour:${t.id}`,
          label: t.name,
          sub: t.tenant?.name ?? undefined,
          group: 'Tours',
          to: `/tours/${t.id}`,
        })),
      );
    }
    return out;
  }, [parsed, debounced, entityQuery.data, hasPerm]);

  // Clamp/reset the selection when the list changes.
  useEffect(() => {
    setSel((s) => Math.min(s, Math.max(0, items.length - 1)));
  }, [items]);

  // Keep the selected row visible.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${sel}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => (items.length ? (s + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => (items.length ? (s - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(items[sel]);
    }
  };

  // Group rows for rendering while keeping one flat index for the keyboard.
  let idx = -1;
  let lastGroup = '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { mt: '10vh' } }}
      // autoFocus below covers the normal open. This backstops the flaky case:
      // a quick close→reopen within the exit transition skips the input's
      // remount, so React's autoFocus never re-fires and MUI's focus trap parks
      // focus on the dialog paper. Refocusing once the enter transition
      // completes puts the cursor back in the search box every time.
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <Box sx={{ p: 1.5, pb: 0 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          inputRef={inputRef}
          placeholder="Go to page, record, user… (try “bus 123” or “create region”)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Command palette search' }}
        />
        <Box sx={{ height: 4, mt: 0.5 }}>
          {entityQuery.isFetching && <LinearProgress sx={{ borderRadius: 1 }} />}
        </Box>
      </Box>
      <List ref={listRef} dense sx={{ maxHeight: '55vh', overflowY: 'auto', pt: 0 }}>
        {items.map((item) => {
          idx += 1;
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const i = idx;
          return (
            <Box key={item.key}>
              {header && (
                <ListSubheader disableSticky sx={{ lineHeight: '28px', bgcolor: 'transparent' }}>
                  {header}
                </ListSubheader>
              )}
              <ListItemButton
                data-idx={i}
                selected={i === sel}
                onClick={() => go(item)}
                onMouseMove={() => setSel(i)}
              >
                <ListItemText primary={item.label} secondary={item.sub} />
              </ListItemButton>
            </Box>
          );
        })}
        {items.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            {debounced.length >= 2 && !entityQuery.isFetching
              ? `No matches for “${debounced}”.`
              : 'Type to search pages and records.'}
          </Box>
        )}
      </List>
      <Typography
        variant="caption"
        sx={{ px: 2, py: 1, color: 'text.secondary', borderTop: 1, borderColor: 'divider' }}
      >
        ↑↓ navigate · ↵ open · esc close
      </Typography>
    </Dialog>
  );
}
